import { useEffect, useState } from 'react';
import {
  Alert,
  Card,
  Group,
  LoadingOverlay,
  SegmentedControl,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { LineChart } from '@mantine/charts';
import type {
  DashboardTimeseries,
  DashboardTimeseriesRange,
} from '@pta/types';
import { fetchGroupTimeseries } from '../api/dashboard';
import { ApiError } from '../api/client';
import { formatNumericString } from '../util/format';

/**
 * Dashboard timeseries chart — egy csoport pénzügyi állapotának
 * időbeli alakulása. Két görbe egy chart-on:
 *   - Egyenleg (running balance) — a csoport számolt aktuális egyenlege.
 *   - Nyitott követelés — kintlevőség (a befizetés-dinamikát kontextusba helyezi).
 *
 * Range választható: 7d, 30d, 90d, 1y. 7d/30d napi, 90d/1y heti bucket.
 */
interface DashboardTimeseriesChartProps {
  groupId: number;
  primaryCurrency: string;
}

export function DashboardTimeseriesChart({
  groupId,
  primaryCurrency,
}: DashboardTimeseriesChartProps) {
  const [range, setRange] = useState<DashboardTimeseriesRange>('30d');
  const [data, setData] = useState<DashboardTimeseries | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    void (async () => {
      try {
        const ts = await fetchGroupTimeseries(groupId, range);
        if (!cancelled) setData(ts);
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof ApiError ? e.message : String(e));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [groupId, range]);

  // A LineChart a `data` tömböt expecteli, ahol minden kulcs egy series.
  // Bucket dátumát rövid címkévé alakítjuk (napi: MM-DD, heti: MM-DD).
  const chartData =
    data?.buckets.map((b) => ({
      date: b.bucketDate.slice(5), // 'MM-DD' formátum
      Egyenleg: Number(b.balance),
      'Nyitott követelés': Number(b.openReceivables),
    })) ?? [];

  return (
    <Card withBorder pos="relative">
      <LoadingOverlay visible={loading} />
      <Group justify="space-between" mb="md">
        <Stack gap={0}>
          <Title order={4}>Egyenleg és kintlevőség alakulása</Title>
          <Text size="xs" c="dimmed">
            {data ? `${data.fromDate} … ${data.toDate} · ${data.granularity === 'daily' ? 'napi' : 'heti'} bontás · ${primaryCurrency}` : ''}
          </Text>
        </Stack>
        <SegmentedControl
          size="xs"
          data={[
            { value: '7d', label: '7 nap' },
            { value: '30d', label: '30 nap' },
            { value: '90d', label: '3 hónap' },
            { value: '1y', label: '1 év' },
          ]}
          value={range}
          onChange={(v) => setRange(v as DashboardTimeseriesRange)}
        />
      </Group>

      {error && <Alert color="red">{error}</Alert>}

      {data && chartData.length > 0 && (
        <LineChart
          h={280}
          data={chartData}
          dataKey="date"
          series={[
            { name: 'Egyenleg', color: 'teal.6' },
            { name: 'Nyitott követelés', color: 'orange.6' },
          ]}
          curveType="monotone"
          withDots={chartData.length <= 31}
          withLegend
          tooltipAnimationDuration={150}
          valueFormatter={(v) => `${formatNumericString(v.toFixed(2))} ${primaryCurrency}`}
        />
      )}

      {data && chartData.length === 0 && (
        <Text size="sm" c="dimmed">
          Nincs adat ebben az időszakban.
        </Text>
      )}
    </Card>
  );
}
